import type { ModelMessageTemplateLite } from '@app/api/MessageTemplates';
import {
  filesExtensions,
  MAX_FILES_SIZE,
} from '@app/components/DraggerComponent/data/constants';
import {
  INSTAGRAM,
  INSTAGRAM_MAX_MESSAGE_LENGTH,
  MAX_FIELD_LENGTH_5000,
} from '@app/constants';

import type {
  InsertTextResult,
  LeadMessageAttachment,
  TemplateVariablesData,
} from './types';

// Composer textarea row bounds. Max rows scale with viewport height so a tall
// screen gets a roomier input without pushing the message list off-screen.
export const COMPOSER_MIN_ROWS = 2;

export const getComposerMaxRows = (viewportHeight: number): number => {
  if (viewportHeight <= 640) return 4;
  if (viewportHeight <= 800) return 6;
  if (viewportHeight <= 960) return 8;

  return 10;
};

// Per-integration message length limit. Mirrors the existing useExtendedMessage
// convention: Instagram is capped tighter (998), every other integration uses
// the default 5000.
export const getMessageMaxLengthBySocialType = (
  socialType?: string | null
): number =>
  socialType === INSTAGRAM
    ? INSTAGRAM_MAX_MESSAGE_LENGTH
    : MAX_FIELD_LENGTH_5000;

const OUTGOING_MESSAGE_EXTRA_EXTENSIONS = ['mp3'];
const TEMPLATE_SEARCH_LIMIT = 20;

const TEMPLATE_VARIABLE_REGEXP =
  /(\{\{\s*([\p{L}\p{N}_]+)\s*\}\}|\{\s*([\p{L}\p{N}_]+)\s*\}|\[\s*([\p{L}\p{N}_]+)\s*\]|%\s*([\p{L}\p{N}_]+)\s*%|<\s*([\p{L}\p{N}_]+)\s*>)/giu;

const normalize = (value?: string | null): string =>
  (value ?? '').trim().toLowerCase();

const getTemplateVariableValue = (
  variableName: string,
  data: TemplateVariablesData
): string | null => {
  const variables: Record<string, string | number | null | undefined> = {
    branch: data.branchName,
    branchname: data.branchName,
    clientid: data.clientId,
    clientname: data.clientFullName,
    email: data.email,
    firstname: data.clientFirstName,
    fullname: data.clientFullName,
    leadid: data.leadId,
    lastname: data.clientLastName,
    middlename: data.clientMiddleName,
    name: data.clientFirstName || data.clientFullName,
    namefirst: data.clientFirstName,
    namelast: data.clientLastName,
    namemiddle: data.clientMiddleName,
    phone: data.phone,
    phone1: data.phone,
    responsible: data.responsibleName,
    responsiblename: data.responsibleName,
  };

  const value = variables[normalize(variableName)];
  if (value === null || value === undefined) return null;

  const stringValue = String(value).trim();

  return stringValue || null;
};

const createAttachmentId = (fileName: string | null): string =>
  `attachment-${Date.now()}-${Math.random().toString(36).slice(2)}-${
    fileName ?? 'file'
  }`;

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Не удалось загрузить файл'));
        return;
      }

      resolve(reader.result.replace(/^data:.+;base64,/, ''));
    };

    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

export const getTemplateSearchQuery = (message: string): string => {
  const value = message.trim();
  if (!value) return '';

  const slashIndex = value.indexOf('/');

  return normalize(slashIndex === -1 ? value : value.slice(slashIndex + 1));
};

export const getTemplateSearchLimit = (): number => TEMPLATE_SEARCH_LIMIT;

export const filterMessageTemplates = (
  templates: ModelMessageTemplateLite[],
  query: string
): ModelMessageTemplateLite[] => {
  const normalizedQuery = normalize(query);
  if (!normalizedQuery) return [];

  return templates.filter(({ Name, Message }) => {
    if (!Message?.trim()) return false;

    return normalize(Name).startsWith(normalizedQuery);
  });
};

export const replaceTemplateVariables = (
  message: string,
  data: TemplateVariablesData
): string =>
  message.replace(TEMPLATE_VARIABLE_REGEXP, (match, ...groups: string[]) => {
    const variableName = groups.find((group) => group && group !== match);
    if (!variableName) return match;

    return getTemplateVariableValue(variableName, data) ?? match;
  });

export const insertTextAtCursor = (
  value: string,
  insertion: string,
  selectionStart?: number | null,
  selectionEnd?: number | null
): InsertTextResult => {
  const start =
    typeof selectionStart === 'number' ? selectionStart : value.length;
  const end = typeof selectionEnd === 'number' ? selectionEnd : start;
  const nextValue = `${value.slice(0, start)}${insertion}${value.slice(end)}`;

  return {
    value: nextValue,
    cursorPosition: start + insertion.length,
  };
};

export const getAllowedAttachmentExtensions = (): string[] => [
  ...filesExtensions,
  ...OUTGOING_MESSAGE_EXTRA_EXTENSIONS,
];

export const getIsAllowedAttachmentExtension = (fileName: string): boolean => {
  const extension = fileName.split('.').pop()?.toLowerCase();

  return Boolean(
    extension && getAllowedAttachmentExtensions().includes(extension)
  );
};

export const normalizeFilesToLeadMessageAttachments = async (
  files: File[]
): Promise<LeadMessageAttachment[]> => {
  const attachments: LeadMessageAttachment[] = [];

  for (const file of files) {
    const data = await readFileAsBase64(file);

    attachments.push({
      id: createAttachmentId(file.name),
      source: 'local',
      Description: null,
      FileName: file.name,
      FileSize: file.size,
      Data: data,
    });
  }

  return attachments;
};

export const getAttachmentsTotalSize = (
  attachments: LeadMessageAttachment[]
): number =>
  attachments.reduce((totalSize, { FileSize }) => totalSize + FileSize, 0);

export const getIsAttachmentsSizeExceeded = (
  attachments: LeadMessageAttachment[]
): boolean => getAttachmentsTotalSize(attachments) > MAX_FILES_SIZE;

export const getIsSendDisabled = ({
  attachments,
  isSending,
  isUploadingFiles,
  messageText,
  selectedIntegrationId,
  selectedLeadId,
}: {
  attachments: LeadMessageAttachment[];
  isSending: boolean;
  isUploadingFiles: boolean;
  messageText: string;
  selectedIntegrationId: string | number | null;
  selectedLeadId: number | null;
}): boolean =>
  isSending ||
  isUploadingFiles ||
  !selectedLeadId ||
  !selectedIntegrationId ||
  (messageText.trim().length === 0 && attachments.length === 0) ||
  getIsAttachmentsSizeExceeded(attachments);

export const formatAttachmentSize = (size: number): string => {
  if (size <= 0) return '';

  const kilobytes = size / 1024;
  if (kilobytes < 1024) return `${kilobytes.toFixed(1)} КБ`;

  return `${(kilobytes / 1024).toFixed(1)} МБ`;
};

export const createTemplateAttachment = ({
  data,
  description,
  fileName,
}: {
  data: string | null;
  description?: string | null;
  fileName: string | null;
}): LeadMessageAttachment => ({
  id: createAttachmentId(fileName),
  source: 'template',
  Description: description ?? null,
  FileName: fileName,
  FileSize: 0,
  Data: data,
});
